import { EMPTY_FORM } from '../constants'
import { editScheduleEditorActions, editScheduleFiltersActions } from '../actions'
import { useEditScheduleGuard } from './useEditScheduleGuard'
import { useAlertClassIds } from './useEditScheduleEffects'
import {
    buildEditFormFromLesson,
    buildNewLessonForm,
    getClassId,
    getLesson,
} from '../utils/lessonHelpers'
import { useActions, useAppSelector } from '../../../store/hooks'
import { editSchedulePageSelector } from '../../../store/slices/editSchedulePage'
import { referencesSelector } from '../../../store/slices/references'

export function useEditScheduleEditorOperations() {
    const { alertClassIds, clearAlertClasses } = useAlertClassIds()

    const viewMode = useAppSelector(editSchedulePageSelector.viewMode)
    const selectedGroupId = useAppSelector(editSchedulePageSelector.selectedGroupId)
    const selectedTeacherId = useAppSelector(editSchedulePageSelector.selectedTeacherId)
    const scheduleFilters = useAppSelector(editSchedulePageSelector.filters)
    const bells = useAppSelector(referencesSelector.bells)
    const pendingAction = useAppSelector(editSchedulePageSelector.pendingAction)
    const editingId = useAppSelector(editSchedulePageSelector.editingId)

    const {
        openEditor,
        resetEditor,
        setPendingAction,
        setShowCloseEditorModal,
        saveLesson,
    } = useActions(editScheduleEditorActions)

    const {
        setViewMode,
        resetSchedule,
        setSelectedGroupId,
        setSelectedTeacherId,
        setDateRange,
        clearDateFilter,
        resetFilters,
        setSelectedBuildId,
        setSelectedRoomId,
        setSelectedClassTypeId,
        setSelectedSubjectId,
        referenceLoadAudience,
    } = useActions(editScheduleFiltersActions)

    const { checkBeforeAction, isFormDirty } = useEditScheduleGuard(editScheduleEditorActions)

    const forceCloseEditor = () => {
        resetEditor()
    }

    const openAddEditor = (skipCheck = false) => {
        if (!skipCheck && !checkBeforeAction('openAdd')) return

        const newForm = buildNewLessonForm({
            emptyForm: EMPTY_FORM,
            selectedGroupId,
            selectedTeacherId,
            defaultBellId: bells[0]?.bell_id || '',
        })

        openEditor({
            form: newForm,
            initialForm: newForm,
            editingId: null,
            isCreating: true,
        })
    }

    const openEditEditor = (item, skipCheck = false) => {
        if (!skipCheck && !checkBeforeAction('openEdit', item)) return

        const lesson = getLesson(item)
        const newForm = buildEditFormFromLesson({
            item,
            lesson,
            viewMode,
            selectedGroupId,
            selectedTeacherId,
            emptyForm: EMPTY_FORM,
        })

        openEditor({
            form: newForm,
            initialForm: newForm,
            editingId: getClassId(item, lesson),
            isCreating: false,
        })
    }

    const tryCloseEditor = () => {
        if (isFormDirty) {
            setPendingAction(null)
            setShowCloseEditorModal({ open: true })
            return false
        }

        forceCloseEditor()
        return true
    }

    const applyPendingAction = (action) => {
        if (!action) return

        clearAlertClasses()

        switch (action.type) {
            case 'openEdit':
                openEditEditor(action.payload, true)
                break
            case 'openAdd':
                openAddEditor(true)
                break
            case 'changeMode':
                setViewMode(action.payload)
                resetSchedule()
                break
            case 'setSelectedGroupId':
                setSelectedGroupId(action.payload)
                break
            case 'setSelectedTeacherId':
                setSelectedTeacherId(action.payload)
                break
            case 'setDateRange':
                setDateRange(action.payload)
                break
            case 'clearDateFilter':
                clearDateFilter()
                break
            case 'resetFilters':
                resetFilters()
                referenceLoadAudience(null)
                break
            case 'setSelectedBuildId':
                setSelectedBuildId(action.payload)
                referenceLoadAudience(action.payload || null)
                break
            case 'setSelectedRoomId':
                setSelectedRoomId(action.payload)
                break
            case 'setSelectedClassTypeId':
                setSelectedClassTypeId(action.payload)
                break
            case 'setSelectedSubjectId':
                setSelectedSubjectId(action.payload)
                break
            default:
                break
        }
    }

    const confirmCloseEditor = () => {
        const action = pendingAction

        forceCloseEditor()
        setShowCloseEditorModal({ open: false })
        applyPendingAction(action)
    }

    const handleSubmit = (form) => {
        const { building_id, group_id, ...rest } = form

        const payload = editingId
            ? {
                ...rest,
                group_id: Array.isArray(group_id) ? group_id[0] ?? '' : group_id,
            }
            : {
                ...rest,
                group_id: Array.isArray(group_id)
                    ? group_id
                    : group_id === '' || group_id === undefined || group_id === null
                        ? []
                        : [group_id],
            }

        saveLesson({
            lessonId: editingId,
            payload,
            reloadClassIds: alertClassIds || null,
            reloadFilters: alertClassIds ? null : scheduleFilters,
        })

        forceCloseEditor()
    }

    return {
        openAddEditor,
        openEditEditor,
        tryCloseEditor,
        confirmCloseEditor,
        handleSubmit,
        isFormDirty,
        pendingAction,
    }
}
